import { StageConstants } from 'app/core/constants';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { mat2, mat4 } from 'gl-matrix';

interface View {
  tilt: number;
  spin: number;
}

interface Dimensions {
  width: number;
  height: number;
}

type SVGRenderer =
    (svg: SVGElement, dimensions: Dimensions) => Promise<HTMLImageElement>;

interface PRNGOptions {
  grabMoveEnabled: boolean;
  grabMoveStep: number;
  fullRotationEnabled: boolean;

  rotationEnabled: boolean;
  rotationDuration: number;

  autoUpdateEnabled: boolean;
  updateInterval: number;

  svgRenderer: SVGRenderer;
}

class PRNGEventEmitter
{
  private callbacks: Array<any> = [];

  public subscribe(callback): void
  {
    this.callbacks.push(callback);
  }

  public unsubscribe(): void
  {
    this.callbacks = [];
  }

  public emit(params): void
  {
    this.callbacks.forEach(callback => callback(params));
  }
}

class PRNGCache
{
  private items: {[key: string]: Promise<string>} = {};
  private itemsResolved: {[key: string]: string} = {};

  public get(key: string): Promise<string>
  {
    if (this.itemsResolved[key]) {
      return new Promise<string>((resolve) => resolve(this.itemsResolved[key]));
    }
    else {
      return new Promise<string>((resolve, reject) =>
        this.items[key].then((value) => {
          this.itemsResolved[key] = value;
          delete this.items[key];

          resolve(value);
        })
        .catch(e => {
          reject(e);
        }));
    }
  }

  public set(key: string, value: Promise<string>): void
  {
    if (this.items[key] === undefined) {
      this.items[key] = value;
    }
  }

  public isCached(key: string): boolean
  {
    return this.items[key] !== undefined ||
        this.itemsResolved[key] !== undefined;
  }
}

export class WebGLComponent
{
  private static readonly VERTEX_SHADER: number = 0;
  private static readonly FRAGMENT_SHADER: number = 1;
  private static readonly RASTER_IMAGE_SIZE_FACTOR: number =
      1.5; // times the canvas size
  private static readonly DRAW_SCENE_RAD: number = 40 * Math.PI / 180;
  private static readonly DRAW_SCENE_TRANSLATION_VECTOR: Array<number> =
      [-0.0, 0.0, -6];
  private static readonly TOP_VIEW: View = { tilt: 0, spin: 0 };
  private static readonly START_VIEW: View = {
    tilt: 60 * Math.PI / 180,
    spin: -45 * Math.PI / 180,
  };
  private static readonly MAX_TILT: number = 80 * Math.PI / 180;
  private static readonly KEY_TILT_STEP: number = 5 * Math.PI / 180;
  private static readonly KEY_SPIN_STEP: number = 10 * Math.PI / 180;
  private static readonly FIT_MARGIN: number = 0.95;
  private static readonly MIN_ZOOM: number = 0.5;
  private static readonly ZOOM_STEP: number = 1.1;
  private static readonly FINALIZE_TIMEOUT: number = 3; // sec
  private static readonly MAX_DEVICE_PIXEL_RATIO: number = 2;
  private static readonly MAX_ANISOTROPY: number = 8;

  public loaded: PRNGEventEmitter = new PRNGEventEmitter();

  protected model: Object;

  private updateProgressChange: PRNGEventEmitter = new PRNGEventEmitter();
  private view: View = { ...WebGLComponent.TOP_VIEW };
  private zoom: number = 1;
  private lidZ: number = 0;
  private modelRadius: number = 1;
  private canvasResizeObserver: ResizeObserver|undefined = undefined;
  private document: ShadowRoot|Document;
  private canvas: HTMLCanvasElement;
  private gl: any;
  private isWebGL2: boolean = false;
  private maxAnisotropy: number = 0;
  private anisotropyExtension: any = undefined;
  private lastXPosition: number;
  private lastYPosition: number;
  private updateScene: number;
  private updateModel: number;
  private activeMove: boolean = false;
  private _updateInProgress: boolean = false;
  private canvasListeners: Object = {};
  private rotationTime: number;
  // Progress of the intro rotation from 0 to 1
  private rotationProgress: number = 0;
  private isLandscape: boolean|undefined = undefined;
  private updateQueued: boolean = false;
  private finalizationInProgress: boolean = false;
  private drawingScene: boolean = false;

  private uniformLocationsCache: {[key: string]: WebGLUniformLocation} = {};

  private imagesCache: PRNGCache = new PRNGCache();
  private fontsCache: PRNGCache = new PRNGCache();
  private svgBlobUrl: string|undefined = undefined;

  private options: PRNGOptions = {
    grabMoveEnabled: true,
    grabMoveStep: 200,
    fullRotationEnabled: false,

    rotationEnabled: true,
    rotationDuration: 2.5,

    autoUpdateEnabled: true,
    updateInterval: 1024,

    svgRenderer: this.renderSvgLocally.bind(this),
  };

  constructor(canvas: HTMLCanvasElement, model: Object, options: Object = {})
  {
    this.canvas = canvas;
    this.document = <any>(canvas.getRootNode());
    this.initGl();

    this.updateProgressChange.subscribe(async (value) => {
      if (!value && this.updateQueued) {
        await this.scheduleUpdate();
      }
    });

    for (let option of Object.keys(this.options)) {
      if (options[option] !== undefined) {
        this.options[option] = options[option];
      }
    }

    this.model = model;
    this.measureModel();
    this.getModelSVGs();
    this.rotateIfRequired();
    this.initModelPart();
  }

  protected async refreshSVGs(): Promise<void>
  {
    this.getModelSVGs();
    this.rotateIfRequired();
    await this.scheduleUpdate();
  }

  public stopAnimation(): void
  {
    window.cancelAnimationFrame(this.updateScene);
    this.permanentRedrawScene();
  }

  private permanentRedrawScene(): void
  {
    this.drawScene();
    this.updateScene =
        window.requestAnimationFrame(this.permanentRedrawScene.bind(this));
  }

  public resumeAnimation(): void
  {
    window.cancelAnimationFrame(this.updateScene);
    this.startAnimation();
  }

  public async scheduleUpdate(): Promise<void>
  {
    if (this.updateInProgress) {
      this.updateQueued = true;
      return;
    }
    this.updateQueued = false;
    await this.update();
    this.drawScene();
    if (!this.updateInProgress && !this.updateQueued) {
      this.loaded.emit(true);
    }
  }

  public async finalize(): Promise<void>
  {
    this.finalizationInProgress = true;
    this.stopDrawingScene();
    this.stopUpdatingModel();
    this.updateProgressChange.unsubscribe();
    while (this.updateInProgress || this.drawingScene) {
      await new Promise<void>(resolve => setTimeout(
          resolve, WebGLComponent.FINALIZE_TIMEOUT * 1000));
    }
    this.deleteWebGLContext();
  }

  private set updateInProgress(updateInProgress: boolean)
  {
    this._updateInProgress = updateInProgress;
    this.updateProgressChange.emit(updateInProgress);
  }

  private get updateInProgress(): boolean
  {
    return this._updateInProgress;
  }

  private getModelSVGs(): void
  {
    for (const part in this.model) {
      if (typeof this.model[part]['svgReference'] === 'string') {
        this.model[part]['svgReference'] =
            this.document.getElementById(this.model[part]['svgReference']);
      }
    }
  }

  // Pivot is the lid centre; the radius keeps the box in view at any angle.
  private measureModel(): void
  {
    const points: Array<Array<number>> = [];
    for (const part of Object.values(this.model)) {
      const position: Array<number> = part['position'] ?? [];
      for (let i = 0; i < position.length; i += 3) {
        points.push(position.slice(i, i + 3));
      }
    }

    this.lidZ = Math.max(...points.map(([, , z]) => z));
    this.modelRadius = Math.max(...points.map(([x, y, z]) =>
        Math.hypot(x, y, z - this.lidZ)));
  }

  /**
   * New models must always be passed in portrait format. This function rotates
   * the model if the first passed SVG object is in landscape format.
   */
  private rotateIfRequired(): void
  {
    const svgElement = this.getFirstSVGElement();
    if (svgElement) {
      const svgDimensions = this.getSVGDimensions(svgElement);
      const isLandscape = svgDimensions.width > svgDimensions.height;

      if (isLandscape === this.isLandscape) {
        return;
      }
      this.isLandscape = isLandscape;
      this.view = { ...WebGLComponent.TOP_VIEW };
      this.rotationProgress = 0;

      if (this.updateScene !== undefined) {
        this.resumeAnimation();
      }
    }
  }

  private getSVGDimensions(svgElement: SVGElement): Dimensions
  {
    const viewBox = svgElement.getAttribute('viewBox').split(' ');
    return {
      width: parseInt(viewBox[2], 10),
      height: parseInt(viewBox[3], 10)
    };
  }

  private getFirstSVGElement(): SVGElement|null
  {
    for (const part in this.model) {
      if (this.model.hasOwnProperty(part)) {
        if (this.model[part]['svgReference']) {
          return this.model[part]['svgReference'];
        }
        break;
      }
    }
  }

  /**
   * @throws Error
   */
  private initGl(): void
  {
    this.gl = this.canvas.getContext('webgl2');
    this.isWebGL2 = !!this.gl;
    if (!this.gl) {
      this.gl = this.canvas.getContext('webgl') ||
          this.canvas.getContext('experimental-webgl');
    }
    if (!this.gl) {
      throw new Error('webgl is not available');
    }

    this.anisotropyExtension =
        this.gl.getExtension('EXT_texture_filter_anisotropic') ||
        this.gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic') ||
        this.gl.getExtension('MOZ_EXT_texture_filter_anisotropic');
    if (this.anisotropyExtension) {
      this.maxAnisotropy = Math.min(
          WebGLComponent.MAX_ANISOTROPY,
          this.gl.getParameter(
              this.anisotropyExtension.MAX_TEXTURE_MAX_ANISOTROPY_EXT));
    }

    this.resizeCanvas();
  }

  private async initModelPart(): Promise<void>
  {
    try {
      this.gl.programs = {};
      this.gl.buffers = {};
      this.gl.textures = {};
      for (const part in this.model) {
        if (this.model.hasOwnProperty(part)) {
          this.gl.programs[part] = this.createProgram(part);
          this.gl.buffers[part] = this.initBuffers(part);
          if (this.model[part]['texture']) {
            this.gl.textures[part] = this.createTexture();
          }
        }
      }
    }
    catch (e) {
      console.error(e);
      return;
    }

    if (this.options.autoUpdateEnabled && this.options.updateInterval > 0) {
      this.updateModel = window.setInterval(() => {
        this.scheduleUpdate();
      }, this.options.updateInterval);
    }
    else {
      await this.scheduleUpdate();
    }

    this.initEventListeners();
    this.startAnimation();
  }

  private initEventListeners(): void
  {
    if (this.options.grabMoveEnabled) {
      this.canvasListeners = {
          pointerdown: this.grabStart.bind(this),
          pointermove: this.grabMove.bind(this),
          pointerup: this.grabEnd.bind(this),
          pointercancel: this.grabEnd.bind(this),
          keydown: this.respondToKey.bind(this),
      };
      this.canvas.style.cursor = 'grab';
      this.canvas.style.touchAction = 'none';
      this.canvas.tabIndex = 0;

      for (const listener in this.canvasListeners) {
        if (this.canvasListeners.hasOwnProperty(listener)) {
          this.canvas.addEventListener(
              listener, this.canvasListeners[listener]);
        }
      }
    }

    this.canvasResizeObserver = new ResizeObserver(() => {
      this.resizeCanvas();
      this.drawScene();
    });
    this.canvasResizeObserver.observe(this.canvas);
  }

  private resizeCanvas(): void
  {
    if (! this.canvas) {
      return;
    }

    const devicePixelRatio = Math.min(
        window.devicePixelRatio || 1, WebGLComponent.MAX_DEVICE_PIXEL_RATIO);
    const width = Math.round(this.canvas.clientWidth * devicePixelRatio);
    const height = Math.round(this.canvas.clientHeight * devicePixelRatio);

    if (width !== this.canvas.width || height !== this.canvas.height) {
      this.canvas.height = height;
      this.canvas.width = width;
    }
  }

  private grabStart(event: PointerEvent): void
  {
    event.stopImmediatePropagation();
    this.canvas.setPointerCapture(event.pointerId);
    this.activeMove = true;
    this.canvas.style.cursor = 'grabbing';
    this.rotationProgress = 1;
    this.stopAnimation();
    this.lastXPosition = event.clientX;
    this.lastYPosition = event.clientY;
  }

  private grabMove(event: PointerEvent): void
  {
    event.stopImmediatePropagation();
    if (this.activeMove) {
      this.applyView({
        tilt: this.view.tilt -
            (event.clientY - this.lastYPosition) / this.options.grabMoveStep,
        spin: this.view.spin +
            (event.clientX - this.lastXPosition) / this.options.grabMoveStep,
      });
    }

    this.lastXPosition = event.clientX;
    this.lastYPosition = event.clientY;
  }

  private grabEnd(event: PointerEvent): void
  {
    event.stopImmediatePropagation();
    this.activeMove = false;
    this.canvas.style.cursor = 'grab';
    this.resumeAnimation();
  }

  private respondToKey(event: KeyboardEvent): void
  {
    const { tilt, spin } = this.view;
    let view = this.view;
    switch (event.key) {
      case 'ArrowUp':
        view = { tilt: tilt + WebGLComponent.KEY_TILT_STEP, spin };
        break;
      case 'ArrowDown':
        view = { tilt: tilt - WebGLComponent.KEY_TILT_STEP, spin };
        break;
      case 'ArrowLeft':
        view = { tilt, spin: spin - WebGLComponent.KEY_SPIN_STEP };
        break;
      case 'ArrowRight':
        view = { tilt, spin: spin + WebGLComponent.KEY_SPIN_STEP };
        break;
      case 'Home':
        view = WebGLComponent.START_VIEW;
        this.zoom = 1;
        break;
      case '+':
        this.zoom = Math.min(1, this.zoom * WebGLComponent.ZOOM_STEP);
        break;
      case '-':
        this.zoom = Math.max(
            WebGLComponent.MIN_ZOOM, this.zoom / WebGLComponent.ZOOM_STEP);
        break;
      default:
        return;
    }
    event.preventDefault();
    this.rotationProgress = 1;
    window.cancelAnimationFrame(this.updateScene);
    this.applyView(view);
  }

  private applyView(view: View): void
  {
    const maxTilt = this.options.fullRotationEnabled
        ? Math.PI
        : WebGLComponent.MAX_TILT;
    this.view = {
      tilt: clamp(view.tilt, 0, maxTilt),
      spin: view.spin,
    };
    this.drawScene();
  }

  private startAnimation(): void
  {
    if (this.options.rotationEnabled && this.rotationProgress < 1) {
      this.rotationTime = Date.now();
      this.rotateScene();
    }
    else {
      this.drawScene();
    }
  }

  private rotateScene(): void
  {
    const currentTime = Date.now();
    this.rotationProgress = Math.min(1, this.rotationProgress +
        ((currentTime - this.rotationTime) / 1000) /
        this.options.rotationDuration);
    const progress = easeInOutSine(this.rotationProgress);
    this.applyView({
      tilt: WebGLComponent.START_VIEW.tilt * progress,
      spin: WebGLComponent.START_VIEW.spin * progress,
    });
    this.rotationTime = currentTime;

    if (this.rotationProgress < 1) {
      this.updateScene =
          window.requestAnimationFrame(this.rotateScene.bind(this));
    }
  }

  private createProgram(part: string): WebGLProgram
  {
    const program = this.gl.createProgram();
    this.gl.attachShader(
        program, this.createShader(WebGLComponent.VERTEX_SHADER, part));
    this.gl.attachShader(
        program, this.createShader(WebGLComponent.FRAGMENT_SHADER, part));
    this.gl.linkProgram(program);
    return program;
  }

  private createShader(shaderType: number, part: string): WebGLShader
  {
    let shader = undefined;
    let shaderSourceCode = undefined;
    if (shaderType === WebGLComponent.VERTEX_SHADER) {
      shaderSourceCode = `
        attribute vec3 position;
        attribute vec3 vertexNormal;
        attribute vec2 texture;

        uniform mat4 normalMatrix;
        uniform mat4 modelViewMatrix;
        uniform mat4 projectionMatrix;

        varying mediump vec2 fTextures;
        varying mediump vec3 fLight;
        void main() {
          fTextures = texture;
          mediump vec4 transformedNormal =
              normalMatrix * vec4(vertexNormal, 1.0);
          mediump float directional = max(dot(
              transformedNormal.xyz, normalize(vec3(-0.4, 0.6, 0.7))), 0.0);
          fLight = vec3(0.3, 0.3, 0.3) + (vec3(1, 1, 1) * directional);

          gl_Position =
              projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `;
      shader = this.gl.createShader(this.gl.VERTEX_SHADER);
    }
    else if (shaderType === WebGLComponent.FRAGMENT_SHADER &&
          this.model[part]['texture']) {
      shaderSourceCode = `
        precision mediump float;

        uniform mat2 textureMatrix;

        varying mediump vec2 fTextures;
        varying mediump vec3 fLight;

        uniform sampler2D ${part};
        void main() {
          mediump vec2 centre = vec2(0.5, 0.5);
          mediump vec2 centeredTexture = fTextures - centre;
          mediump vec2 rotatedTexture = textureMatrix * centeredTexture;
          mediump vec4 textureColor =
              texture2D(${part}, rotatedTexture + centre);
          gl_FragColor = vec4(textureColor.rgb * fLight, textureColor.a);
        }
      `;
      shader = this.gl.createShader(this.gl.FRAGMENT_SHADER);
    }
    else {
      shaderSourceCode = `
        precision mediump float;

        uniform vec3 color;

        varying mediump vec3 fLight;
        void main() {
          gl_FragColor = vec4(color.rgb * fLight, 1);
        }
      `;
      shader = this.gl.createShader(this.gl.FRAGMENT_SHADER);
    }

    this.gl.shaderSource(shader, shaderSourceCode);
    this.gl.compileShader(shader);
    if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
      console.error('creating shader failed', this.gl.getShaderInfoLog(shader));
    }
    return shader;
  }

  private initBuffers(part: string): Object
  {
    const buffers = {
      position: [
          this.generateArrayBuffer(this.model[part]['position']),
          3
      ],
      element: [
          this.generateElementArrayBuffer(this.model[part]['element']),
          this.model[part]['element'].length
      ],
      vertexNormal: [
          this.generateArrayBuffer(this.model[part]['vertexNormal']),
          3
      ]
    };
    if (this.model[part]['texture']) {
      buffers['texture'] =
          [ this.generateArrayBuffer(this.model[part]['texture']), 2 ];
    }
    if (this.model[part]['color']) {
      buffers['color'] =
          [ this.generateArrayBuffer(this.model[part]['color']), 3 ];
    }
    return buffers;
  }

  private generateArrayBuffer(sizes: Array<number>): WebGLBuffer
  {
    const buffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, buffer);
    this.gl.bufferData(
        this.gl.ARRAY_BUFFER, new Float32Array(sizes), this.gl.STATIC_DRAW);
    return buffer;
  }

  private generateElementArrayBuffer(sizes: Array<number>): WebGLBuffer
  {
    const buffer = this.gl.createBuffer();
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, buffer);
    this.gl.bufferData(
        this.gl.ELEMENT_ARRAY_BUFFER,
        new Uint16Array(sizes),
        this.gl.STATIC_DRAW);
    return buffer;
  }

  private createTexture(): WebGLTexture
  {
    const texture = this.gl.createTexture();
    this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, 1, 1, 0,
        this.gl.RGBA, this.gl.UNSIGNED_BYTE,
        new Uint8Array([243, 243, 243, 255]));
    return texture;
  }

  private cloneSVG(SVGImage: SVGElement): SVGElement
  {
    const newSVGImage = <SVGElement>SVGImage.cloneNode(true);
    this.restoreOriginalViewBox(newSVGImage);

    return newSVGImage;
  }

  private removeFromSVGBySelector(svg: SVGElement, selector: string): void
  {
    const elements = svg.querySelectorAll(selector);

    for (let i = 0; i < elements.length; i++) {
      const element = elements.item(i);
      element.parentNode.removeChild(element);
    }
  }

  private restoreOriginalViewBox(svg: SVGElement): void
  {
    const papViewBox = 'pap:original-viewBox';

    if (! svg.hasAttribute(papViewBox)) {
      return;
    }

    svg.setAttribute('viewBox', svg.getAttribute(papViewBox));
  }

  private async loadExternalData(
      svgReference: SVGElement, SVGDimensions: Dimensions): Promise<void>
  {
    const dataLoaded: Array<Promise<void>> = [];
    dataLoaded.push(this.loadImages(svgReference, SVGDimensions));
    dataLoaded.push(this.loadFonts(svgReference));
    await Promise.all(dataLoaded);
  }

  private async loadImages(SVGImage: SVGElement, SVGDimensions: Dimensions
      ): Promise<void>
  {
    const imagesLoaded: Array<Promise<void>> = [];

    const images = SVGImage.getElementsByTagName('image');
    for (let i = 0; i < images.length; i++)
    {
      const image = images.item(i);
      const imageUrl = this.getImageUrl(image, SVGDimensions);

      if (!imageUrl) {
        continue;
      }

      imagesLoaded.push(new Promise((resolveLoaded) => {
        if (!this.imagesCache.isCached(imageUrl)) {
          this.imagesCache.set(imageUrl, new Promise((resolve, reject) => {
              this.loadUrlToBase64(<string>imageUrl).then(base64Image => {
                resolve(base64Image);
              }).catch(e => {
                reject(e);
              });
            })
          );
        }

        this.imagesCache.get(imageUrl).then(b64 => {
          image.setAttributeNS('http://www.w3.org/1999/xlink', 'href', b64);
          resolveLoaded();
        })
        .catch(() => {
          console.error('could not load image: ' + imageUrl);
        });
      }));
    }

    await Promise.all(imagesLoaded);
  }

  private getImageUrl(image: SVGElement, SVGDimensions: Dimensions): string|void
  {
    // TODO: As soon as the ImageStore is no longer in use anywhere, this split
    // can be removed.
    let imageUrl = image.getAttributeNS(
        'http://www.w3.org/1999/xlink', 'href');
    if (!imageUrl || imageUrl.startsWith('data')) {
      return;
    }

    imageUrl = imageUrl.split('/versions/')[0];

    const limit = {
        width: Utils.steppedLimit(Math.ceil(
            this.canvas.width * WebGLComponent.RASTER_IMAGE_SIZE_FACTOR)),
    };
    const url = new URL(imageUrl);
    url.searchParams.set('limit', JSON.stringify(limit));
    return url.toString();
  }

  /**
   * @throws Error
   */
  private loadUrlToBase64(url: string): Promise<string>
  {
    return new Promise<string>((resolve, reject) => {
      let request = new XMLHttpRequest();
      request.open('GET', url, true);
      request.responseType = 'blob';
      request.onreadystatechange = () => {
        if (request.readyState === XMLHttpRequest.DONE) {
          if (request.status === 200) {
            const reader = new FileReader();
            reader.onload = () => {
              resolve(reader.result as string);
            };
            reader.readAsDataURL(request.response);
          }
          else {
            reject(request);
          }
        }
      };
      request.send();
    });
  }

  private async loadFonts(svgReference: SVGElement): Promise<void>
  {
    const fontsContainers = Array.from(
      svgReference.querySelectorAll('style[data-is-fonts-container=\'yes\']')
    );

    for (const fontsContainer of fontsContainers) {
      await this.loadFontsFromContainer(fontsContainer);
    }
  }

  private async loadFontsFromContainer(fontsContainer: Element): Promise<void>
  {
    const fontsLoaded: Array<Promise<void>> = [];
    const svgString = new XMLSerializer().serializeToString(fontsContainer);

    for (const fontUrl of (svgString.match(/url\(('|").*('|")\)/g) || [])) {
      fontsLoaded.push(new Promise((resolveLoaded) => {
        if (!this.fontsCache.isCached(fontUrl)) {
          this.fontsCache.set(fontUrl, new Promise((resolve, reject) => {
            this.loadUrlToBase64(fontUrl.split(/"|'/g)[1]).then(base64Font => {
              resolve(base64Font);
            }).catch(e => {
              reject(e);
            });
          }));
        }

        this.fontsCache.get(fontUrl).then((b64) => {
          fontsContainer.innerHTML =
              fontsContainer.innerHTML.replace(fontUrl, 'url("' + b64 + '")');
          resolveLoaded();
        })
        .catch(() => {
          console.error('could not load font: ' + fontUrl);
        });
      }));
    }

    await Promise.all(fontsLoaded);
  }

  private updateTexture(part: string, rasterImage: HTMLImageElement): void
  {
    try {
      this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA,
          this.gl.UNSIGNED_BYTE, rasterImage);
    }
    catch (e) {
      console.error('updating texture failed', e);
    }

    this.gl.texParameteri(this.gl.TEXTURE_2D,
        this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D,
        this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE);
    this.gl.texParameteri(this.gl.TEXTURE_2D,
        this.gl.TEXTURE_MAG_FILTER, this.gl.LINEAR);

    if (this.canUseMipmaps(rasterImage)) {
      this.gl.generateMipmap(this.gl.TEXTURE_2D);
      this.gl.texParameteri(this.gl.TEXTURE_2D,
          this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR_MIPMAP_LINEAR);
      if (this.anisotropyExtension && this.maxAnisotropy > 1) {
        this.gl.texParameterf(this.gl.TEXTURE_2D,
            this.anisotropyExtension.TEXTURE_MAX_ANISOTROPY_EXT,
            this.maxAnisotropy);
      }
    } else {
      this.gl.texParameteri(this.gl.TEXTURE_2D,
          this.gl.TEXTURE_MIN_FILTER, this.gl.LINEAR);
    }
  }

  private canUseMipmaps(rasterImage: HTMLImageElement): boolean
  {
    return this.isWebGL2 || (this.isPowerOf2(rasterImage.width)
        && this.isPowerOf2(rasterImage.height));
  }

  /**
   * @throws Error
   */
  private convertSVGToHTMLImage(
      svgReference: SVGElement,
      targetDimensions: Dimensions): Promise<HTMLImageElement>
  {
    return new Promise(async (resolve, reject) => {
      const svgString = StageUtils.sanitizeStructure(
          StageUtils.withoutNonBreakingSpaces(
              new XMLSerializer().serializeToString(svgReference)));
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml'});
      const url = URL.createObjectURL(svgBlob);

      const rasterImage = new Image();
      (<any>rasterImage).decoding = 'sync';
      rasterImage.crossOrigin = 'anonymous';
      rasterImage.width = targetDimensions.width;
      rasterImage.height = targetDimensions.height;
      rasterImage.onload = () => resolve(rasterImage);
      rasterImage.onerror = e => reject(e);
      rasterImage.setAttribute('src', url);
      if (this.svgBlobUrl) {
        URL.revokeObjectURL(this.svgBlobUrl);
      }
      this.svgBlobUrl = url;
    });
  }

  private redrawTextureForPart(
      partName: string,
      rasterImage: HTMLImageElement,
      rasterImageDimensions: Dimensions): void
  {
    const part = this.model[partName];

    this.gl.bindTexture(this.gl.TEXTURE_2D, this.gl.textures[partName]);

    if (JSON.stringify(rasterImageDimensions) !==
        JSON.stringify(part.rasterImageDimensions)) {
      this.updateTexture(partName, rasterImage);
      part.rasterImageDimensions = rasterImageDimensions;
    }
    else {
      this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, this.gl.RGBA,
          this.gl.UNSIGNED_BYTE, rasterImage);
      if (this.canUseMipmaps(rasterImage)) {
        this.gl.generateMipmap(this.gl.TEXTURE_2D);
      }
    }
  }

  private async renderSvgLocally(
      svgReference: SVGElement, targetDimensions: Dimensions):
      Promise<HTMLImageElement>
  {
    await this.loadExternalData(svgReference, targetDimensions);

    try {
      return await this.convertSVGToHTMLImage(svgReference, targetDimensions);
    }
    catch (e) {
      console.error('vector graphic to raster graphic conversion failed', e);
    }
  }

  private getRasterImageDimensions(viewBox: Array<string>): Dimensions
  {
    let maxWidth: number;
    let maxHeight: number;

    const width = parseInt(viewBox[2], 10);
    const height = parseInt(viewBox[3], 10);
    if (width > height) {
      maxWidth = this.canvas.width * WebGLComponent.RASTER_IMAGE_SIZE_FACTOR;
      maxHeight = maxWidth / width * height;
    }
    else {
      maxHeight = this.canvas.height * WebGLComponent.RASTER_IMAGE_SIZE_FACTOR;
      maxWidth = maxHeight / height * width;
    }

    const maxTextureSize = this.gl.getParameter(this.gl.MAX_TEXTURE_SIZE);
    const downscale = Math.min(
        1, maxTextureSize / Math.max(maxWidth, maxHeight));

    return {
      width: Math.max(1, Math.floor(maxWidth * downscale)),
      height: Math.max(1, Math.floor(maxHeight * downscale)),
    };
  }

  private isPowerOf2(value): boolean
  {
    return (value & (value - 1)) === 0;
  }

  private getStageChecksum(stageRef: any): any
  {
    // shamelessly stolen from
    // https://stackoverflow.com/questions/7616461/
    //    generate-a-hash-from-string-in-javascript-jquery#7616484

    if (! stageRef) {
      return 0;
    }

    const str = stageRef.innerHTML;
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return hash;
  }

  private async update(): Promise<void>
  {
    this.updateInProgress = true;

    try {
      for (const partName in this.model) {
        if (this.model.hasOwnProperty(partName)) {
          const part = this.model[partName];

          if (part.svgReference) {
            if (part.lastChecksum && part.updateFrequency === 'startup') {
              continue;
            }

            const svgReference =
                <SVGElement>this.document.querySelector(
                    '#' + part.svgReference.id) ||
                part.svgReference;
            part.svgReference = svgReference;

            const checksum = this.getStageChecksum(svgReference);

            if (part.lastChecksum === undefined ||
                part.lastChecksum !== checksum)
            {
              const cleanedSvg = this.cloneSVG(svgReference);

              const rasterImageDimensions = this.getRasterImageDimensions(
                  cleanedSvg.getAttribute('viewBox').split(' '));

              const rasterImage = await this.options.svgRenderer(
                  cleanedSvg, rasterImageDimensions);

              this.redrawTextureForPart(
                  partName, rasterImage, rasterImageDimensions);
            }

            part.lastChecksum = checksum;
          }

          const errorCode = this.gl.getError();
          if (errorCode !== 0) {
            console.error('webgl error by updating `' + partName + '`: ' +
                          errorCode);
          }
        }
      }
    }
    catch (e) {
      console.error('update failed', e);
    }
    finally {
      this.updateInProgress = false;
    }
  }

  private drawScene(): void
  {
    if (this.finalizationInProgress) {
      return;
    }
    try {
      this.drawingScene = true;

      this.gl.enable(this.gl.DEPTH_TEST);
      this.gl.depthFunc(this.gl.LEQUAL);
      this.gl.clearColor(0, 0, 0, 0);
      this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
      this.gl.viewport(
          0, 0, this.gl.drawingBufferWidth, this.gl.drawingBufferHeight);

      const aspect = this.canvas.clientWidth / this.canvas.clientHeight;
      const halfFieldOfView = WebGLComponent.DRAW_SCENE_RAD / 2;
      const fieldOfView = aspect >= 1
          ? WebGLComponent.DRAW_SCENE_RAD
          : 2 * Math.atan(Math.tan(halfFieldOfView) / aspect);

      const projectionMatrix = mat4.create();
      mat4.perspective(projectionMatrix, fieldOfView, aspect, 0.1, 100);

      const cameraDistance =
          -WebGLComponent.DRAW_SCENE_TRANSLATION_VECTOR[2];
      const scale = this.zoom * WebGLComponent.FIT_MARGIN *
          cameraDistance * Math.sin(halfFieldOfView) / this.modelRadius;

      const orientation = this.isLandscape ? Math.PI / 2 : 0;
      const modelViewMatrix = mat4.create();
      mat4.translate(modelViewMatrix, modelViewMatrix,
          WebGLComponent.DRAW_SCENE_TRANSLATION_VECTOR);
      mat4.rotateX(modelViewMatrix, modelViewMatrix, -this.view.tilt);
      mat4.rotateZ(
          modelViewMatrix, modelViewMatrix, this.view.spin + orientation);
      mat4.scale(modelViewMatrix, modelViewMatrix, [scale, scale, scale]);
      mat4.translate(modelViewMatrix, modelViewMatrix, [0, 0, -this.lidZ]);

      const textureMatrix = mat2.create();
      mat2.rotate(textureMatrix, textureMatrix, -orientation);

      const normalMatrix = mat4.create();
      mat4.invert(normalMatrix, modelViewMatrix);
      mat4.transpose(normalMatrix, normalMatrix);

      for (const part in this.model) {
        if (this.model.hasOwnProperty(part)) {
          this.drawPart(
              part,
              Array.from(projectionMatrix),
              Array.from(modelViewMatrix),
              Array.from(normalMatrix),
              Array.from(textureMatrix));
        }
      }

    }
    finally {
      this.drawingScene = false;
    }
  }

  private drawPart(part: string, projectionMatrix: Array<number>,
      modelViewMatrix: Array<number>, normalMatrix: Array<number>,
      textureMatrix: Array<number>): void
  {
    const program = this.gl.programs[part];
    this.gl.useProgram(program);
    if (!this.gl.getProgramParameter(program, this.gl.LINK_STATUS)) {
      return;
    }

    this.gl.uniformMatrix4fv(
        this.getUniformLocation(program, 'projectionMatrix', part),
        false, projectionMatrix);
    this.gl.uniformMatrix4fv(
        this.getUniformLocation(program, 'modelViewMatrix', part),
        false, modelViewMatrix);
    this.gl.uniformMatrix4fv(
        this.getUniformLocation(program, 'normalMatrix', part),
        false, normalMatrix);
    this.gl.uniformMatrix2fv(
        this.getUniformLocation(program, 'textureMatrix', part),
        false, textureMatrix);
    this.bindArrayBuffer(program, part, 'position');
    this.bindArrayBuffer(program, part, 'vertexNormal');
    if (this.model[part]['texture']) {
      this.bindArrayBuffer(program, part, 'texture');
      this.bindTexture(program, part);
    }
    if (this.model[part]['color']) {
      this.gl.uniform3fv(
          this.getUniformLocation(program, 'color', part),
          this.model[part]['color']);
    }
    this.bindElementArrayBuffer(part);
  }

  private getUniformLocation(program: WebGLProgram, name: string, part: string
      ): WebGLUniformLocation
  {
    const idx = part + '-' + name;

    if (this.uniformLocationsCache[idx] === undefined) {
      this.uniformLocationsCache[idx] =
          this.gl.getUniformLocation(program, name);
    }
    return this.uniformLocationsCache[idx];
  }

  private bindArrayBuffer(
      program: WebGLProgram, part: string, bufferName: string): void
  {
    this.gl.bindBuffer(
        this.gl.ARRAY_BUFFER, this.gl.buffers[part][bufferName][0]);
    const attribLocation = this.gl.getAttribLocation(program, bufferName);
    this.gl.vertexAttribPointer(
        attribLocation, this.gl.buffers[part][bufferName][1], this.gl.FLOAT,
        false, 0, 0);
    this.gl.enableVertexAttribArray(attribLocation);
  }

  private bindTexture(
      program: WebGLProgram, textureName: string): void
  {
    // textureName and part are identical
    this.gl.uniform1i(
        this.getUniformLocation(program, textureName, textureName), 0);
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.gl.textures[textureName]);
  }

  private bindElementArrayBuffer(part: string): void
  {
    this.gl.bindBuffer(
        this.gl.ELEMENT_ARRAY_BUFFER, this.gl.buffers[part]['element'][0]);
     this.gl.drawElements(
        this.gl.TRIANGLES,
        this.gl.buffers[part]['element'][1],
        this.gl.UNSIGNED_SHORT,
        0);
  }

  private stopDrawingScene(): void
  {
    window.cancelAnimationFrame(this.updateScene);
    for (const listener in this.canvasListeners) {
      if (this.canvasListeners.hasOwnProperty(listener)) {
        this.canvas.removeEventListener(
            listener, this.canvasListeners[listener]);
      }
    }
    this.canvas.style.cursor = '';
    if (this.canvasResizeObserver) {
      this.canvasResizeObserver.disconnect();
      this.canvasResizeObserver = undefined;
    }
  }

  private stopUpdatingModel(): void
  {
    clearInterval(this.updateModel);
  }

  private deleteWebGLContext(): void
  {
    for (const part in this.model) {
      if (this.model.hasOwnProperty(part)) {
        for (const buffer in this.gl.buffers[part]) {
          if (this.gl.buffers[part].hasOwnProperty(buffer)) {
            this.gl.deleteBuffer(this.gl.buffers[part][buffer][0]);
          }
        }
        if (this.gl.textures[part]) {
          this.gl.deleteTexture(this.gl.textures[part]);
        }
        this.gl.getAttachedShaders(this.gl.programs[part]).forEach(shader => {
          this.gl.deleteShader(shader);
        });
        this.gl.deleteProgram(this.gl.programs[part]);
      }
    }
  }
}

function easeInOutSine(progress: number): number
{
  return -(Math.cos(Math.PI * progress) - 1) / 2;
}

function clamp(value: number, min: number, max: number): number
{
  return Math.min(max, Math.max(min, value));
}
